import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewChildren,
  ViewContainerRef,
  ViewEncapsulation
} from '@angular/core';
import { ObjectDefinitionModel } from '@base/modules/rest/bbs-object/model/object-definition.model';
import { FormControl } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MatDialog } from '@angular/material/dialog';
import {
  ObjectInDialogComponent,
  ObjectInDialogData
} from '@views/secured/object/components/object-in-dialog/object-in-dialog.component';
import { DxDataGridComponent, DxDataGridTypes } from 'devextreme-angular/ui/data-grid';
import { ColumnModel, ColumnType } from '@shared/components/beyond-table/model/column.model';
import ArrayStore from 'devextreme/data/array_store';
import { TranslateService } from '@ngx-translate/core';
import {
  exportTableData,
  groupColumns,
  handleColumnFilterValues,
  NEW__,
  objectColumns,
  saveChangedColumnReordering,
  saveChangedColumnWidth,
  UPDATED__
} from '@base/utils/table-data.util';
import { of, Subject, switchMap } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs/operators';
import { CustomFieldModel } from '@base/modules/rest/custom-field/model/custom-field.model';
import { ObjectCompositeFilterModel } from '@base/modules/rest/object/model/object-composite-filter.model';
import { createFieldFilter, mergeCompositeFilters } from '@views/secured/object/utils/composite-filters.util';
import {
  capitalizePath,
  isPath,
  pathGetLast,
  pathSkipFirst,
  pathSkipLast,
  splitFieldPaths,
  valueOnPath
} from '@base/utils/common.util';
import { generateUUID } from '@base/utils/uuid';
import { ItemOptionsModel } from '@views/secured/object/components/object/model/item-options.model';
import { ItemActionParamsModel } from '@views/secured/object/components/object/model/item-action-params.model';
import { ItemActionModel } from '@views/secured/object/components/object/model/item-action.model';
import { ItemActionEnum } from '@views/secured/object/components/object/enums/item-action.enum';
import { UserContext } from '@base/services/user-context';
import { ObjectNamePipe } from '@shared/pipes/object-name.pipe';
import { ItemEditingCellStartModel } from '@views/secured/object/components/object/model/item-editing-cell-start.model';
import { ItemEditingCellEndModel } from '@views/secured/object/components/object/model/item-editing-cell-end.model';
import { CustomFieldTypesEnum } from '@base/modules/rest/custom-field/enums/custom-field-types.enum';
import { now } from '@base/utils/date.util';
import { LoadItemsSettingsModel } from '@views/secured/object/components/object/model/load-items-settings.model';
import { ObjectRestServiceCreator } from '@base/modules/rest/object/object-rest-service-creator';
import { PermissionsService } from '@base/modules/security/services/permissions.service';
import { PermissionActionEnum } from '@base/modules/rest/permissions/enums/permission-action.enum';
import { ItemTableRowClickModel } from '@views/secured/object/components/object/model/item-table-row-click.model';
import {
  TableLookupDropdownItemComponent
} from '@modules/master-data/components/items-table/table-lookup-dropdown-item/table-lookup-dropdown-item.component';
import DxDataGrid, {
  CellClickEvent,
  CellPreparedEvent,
  InitializedEvent,
  OptionChangedEvent,
  SelectionChangedEvent
} from 'devextreme/ui/data_grid';
import { IdModel } from '@base/modules/rest/master-data/model/id.model';
import {
  DeleteConfirmationDialogComponent,
  DeleteConfirmationDialogData
} from '@shared/components/dialogs/delete-confirmation-dialog/delete-confirmation-dialog.component';
import { BUI_ICON_TYPE_CUSTOM } from '@base/utils/icon-types.util';
import {
  FormattingDialogComponent
} from '@views/secured/beyond-smart-reports/views/report/components/devextreme/formatting-dialog/formatting-dialog.component';
import {
  DataGridObjectFilterModel
} from '@views/secured/beyond-smart-reports/views/report/components/devextreme/model/data-grid-object-filter.model';
import {
  generateConditionalFormatting
} from '@views/secured/beyond-smart-reports/views/report/components/devextreme/utils/data-grid-conditional-formatting';
import {
  DataGridCellObjectModel
} from '@views/secured/beyond-smart-reports/views/report/components/devextreme/model/data-grid-cell-object.model';
import { getColumnNumber } from '@shared/util/util';
import {
  SummaryTypeEnum
} from '@views/secured/beyond-smart-reports/views/report/components/devextreme/summaries-dialog/enum/summary-type.enum';
import {
  SummaryFilterModel
} from '@views/secured/beyond-smart-reports/views/report/components/devextreme/summaries-dialog/model/summary-filter.model';
import {
  SummariesDialogComponent
} from '@views/secured/beyond-smart-reports/views/report/components/devextreme/summaries-dialog/summaries-dialog.component';
import { DevExtremeFieldTypesEnum } from '@views/secured/beyond-smart-reports/enums/dev-extreme-field-types.enum';
import { round } from '@base/utils/number.util';
import { ObjectNameEnum } from '@base/modules/rest/master-data-history/model/enums/object-name.enum';
import { BeyondTabsService } from '@shared/components/beyond-tabs/beyond-tabs.service';
import { off, on } from 'devextreme/events';
import { DELETE, END, HOME } from '@angular/cdk/keycodes';
import {
  MasterDataCardsComponent
} from '@modules/master-data/components/master-data-cards/master-data-cards.component';
import { RecordDisplayValuePipe } from '@base/pipes/record-display-value.pipe';
import {
  ItemsTableHeaderPopupService
} from '@modules/master-data/components/items-table/service/items-table-header-popup.service';
import {
  ItemsTableHeaderDebugService
} from '@modules/master-data/components/items-table/service/items-table-header-debug.service';
import { ActionResponseModel } from '@base/modules/rest/model/action-response.model';
import { BeyondPaginationModel } from '@base/model/common/beyond-pagination.model';
import { BeyondTableActions } from '@shared/components/beyond-table/util/beyond-table-actions';
import { Overlay } from '@angular/cdk/overlay';
import {
  ActiveFiltersHeightTypeEnum
} from '@modules/master-data/components/active-filters-display/active-filters.component';
import { FieldDebugService } from '@base/services/field-debug.service';
import {
  FilterItemChangedModel
} from '../master-data-records/master-data-records-filters/model/filter-item-changed.model';
import { FieldSettingsDialogComponent } from '../../dialogs/field-settings-dialog/field-settings-dialog.component';
import { isEditMode, ModeEnum } from '../../model/mode.enum';
import {
  getFirstEditableColumn,
  getRowFromVisibleRows,
  ItemsTableLookupColumnHelper
} from './service/items-table-lookup-column-helper';
import { ItemsTableValidation } from './service/items-table-validation';
import { ItemsTableSelectionChangeModel } from './model/items-table-selection-change.model';

const DEVEXTREME_KEY_PROPERTY = '_id';
const DEBOUNCE_TIME = 600;
const F6_KEY_CODE = 117;

interface CellInputEvent {
  key: string;
  dataField: string;
  field: CustomFieldModel;
  rowIndex: number;
  columnIndex: number;
  value: string;
}

interface LastCellValue {
  dataField: string;
  rowIndex: number;
  columnIndex: number;
  value: any;
}

export interface ItemsTableTabModel {
  id: number;
  label: string;
}

export enum ItemsTableHeightTypeEnum {
  AUTO = 'auto',
  FULL = 'full',
  FIXED = 'fixed',
}

const saveTableHeight = (objectName: string, height: string) => {
  const tableHeights = JSON.parse(localStorage.getItem('tableHeights')) || {};
  tableHeights[objectName] = height;
  localStorage.setItem('tableHeights', JSON.stringify(tableHeights));
};

const getTableHeight = (objectName: string): string => {
  const tableHeights = JSON.parse(localStorage.getItem('tableHeights')) || {};
  return tableHeights[objectName];
};

const MQ_BELOW_NARROW = '(max-width: 720px)';

@UntilDestroy()
@Component({
  selector: 'app-items-table',
  templateUrl: './items-table.component.html',
  styleUrls: ['./items-table.component.scss'],
  // eslint-disable-next-line @angular-eslint/use-component-view-encapsulation
  encapsulation: ViewEncapsulation.None,
})
export class ItemsTableComponent<T extends IdModel = any> implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() detailTable = true;
  @Input() virtualScroll = true;
  @Input() showPanelWrapper = false;
  @Input() showTableWrapper = true;
  @Input() showTabsWrapper = false;
  @Input() tabs: ItemsTableTabModel[];
  @Input() idPath = 'id';
  @Input() parent: any;
  @Input() isPaginationEnabled: boolean;
  @Input() pagination: BeyondPaginationModel;
  @Input() itemOptions: ItemOptionsModel;
  @Input() standalone = true;
  @Input() viewActions = false;
  @Input() mode: ModeEnum;
  @Input() noIdentifier: boolean;
  @Input() showSelection = true;
  @Input() multiSelection = true;
  @Input() showSelectAll = true;
  @Input() multiSort = false;
  @Input() objectDefinition: ObjectDefinitionModel;
  @Input() filters: CustomFieldModel[];
  @Input() title: string;
  @Input() initNewItemFn: (data: any) => void;
  @Input() disableRowFn: (data: any) => boolean;
  @Input() disableAddItemFn: () => boolean;
  @Input() items: T[];
  @Input() deleted__: { [key: string]: any[] };
  @Input() showColumnsSum: boolean;
  @Input() chooserForNew = true;
  @Input() preventAdd: boolean;
  @Input() preventDelete: boolean;
  @Input() autoNewRow = false;
  @Input() incrementField: string;
  @Input() groupByColumns: string[];
  @Input() height: ItemsTableHeightTypeEnum = ItemsTableHeightTypeEnum.FIXED;
  @Input() preventRowClick = false;
  @Input() filterRowVisible = true;
  @Input() filterHeaderVisible = true;
  @Input() groupPanelVisible = true;
  @Input() allowReordering = true;
  @Input() openFiltersByDefault: boolean;
  @Input() createAnotherItem = false;
  @Input() preventSaveColumnChanges = false;
  @Input() openLookupFn: (objectName: ObjectNameEnum, id: number) => void;
  @Input() compositeFilter: ObjectCompositeFilterModel;
  @Input() handleAddAction = false;
  @Input() partialInlineEdit: boolean;
  @Input() partialInlineEditColumns: string[];
  @Output() itemActionEvent = new EventEmitter<ItemActionParamsModel>();
  @Output() paginationChangedEvent = new EventEmitter<BeyondPaginationModel>();

  @ViewChild(DxDataGridComponent, {static: false}) dataGrid: DxDataGridComponent;
  @ViewChild('dataGridWrapperDiv') dataGridWrapperDiv: ElementRef;
  @ViewChild('hideDropzoneItem') hideDropzoneItem: ElementRef;
  @ViewChild('dropdownList') dropdownList: ElementRef;
  @ViewChildren(TableLookupDropdownItemComponent) dropdownItems: QueryList<TableLookupDropdownItemComponent>;
  @ViewChild(MasterDataCardsComponent) cardsComponent: MasterDataCardsComponent;
  @ViewChild('columnFixPopup') columnFixPopup: TemplateRef<any>;
  @ViewChild('columnDebugPopup') columnDebugPopup: TemplateRef<any>;
  @ViewChild('actionsDropdown') actionsDropdownTemplate: TemplateRef<any>;

  protected userContext = inject(UserContext);

  ItemActionEnum = ItemActionEnum;
  PermissionActionEnum = PermissionActionEnum;
  inlineEditing: boolean;
  isEditMode: boolean;
  isValidation = false;
  filterFormControl: FormControl;
  filteredItems: T[] = [];
  columns: ColumnType[] = [];
  dataSource: ArrayStore;
  lookupInputChanged$ = new Subject<CellInputEvent>();
  lastEditedDataField: string;
  columnResized = false;
  lastEvent: any;
  hasAddPermission = false;
  columnHeaders: HTMLElement[];
  draggedColumn: any;
  ItemsTableHeightTypeEnum = ItemsTableHeightTypeEnum;
  itemsTableLookupColumnHelper: ItemsTableLookupColumnHelper = new ItemsTableLookupColumnHelper(this, this.cdr);
  itemsTableValidation: ItemsTableValidation = new ItemsTableValidation(this);
  itemsTableHeaderPopupService: ItemsTableHeaderPopupService = new ItemsTableHeaderPopupService();
  itemsTableHeaderDebugService: ItemsTableHeaderDebugService = new ItemsTableHeaderDebugService();
  lastCellData: LastCellValue;
  fieldsCacheMap = new Map<string, CustomFieldModel>();
  dropZoneStyle: { [key: string]: string } = {};
  conditionalFormattingFilters: DataGridObjectFilterModel[] = [];
  summaryFilters: SummaryFilterModel[] = [];
  editOnkeyPress = true;
  enterKeyAction: DxDataGridTypes.EnterKeyAction = 'startEdit';
  enterKeyDirection: DxDataGridTypes.EnterKeyDirection = 'row';
  hasSelectedRows = false;
  mqBelowNarrow = MQ_BELOW_NARROW;
  screenSizeSmall = false;
  allowFocusNewRow = false;
  recordDisplayValuePipe = new RecordDisplayValuePipe();
  showDevExtremeFilterRow: boolean = this.userContext.parameters.showDevExtremeFilterRow;
  tableActions: BeyondTableActions;
  activeFiltersHeight: ActiveFiltersHeightTypeEnum = ActiveFiltersHeightTypeEnum.COLLAPSED;
  fieldDebugEnabled = false;

  constructor(private matDialog: MatDialog,
              private translate: TranslateService,
              private objectRestServiceCreator: ObjectRestServiceCreator,
              private permissionsService: PermissionsService,
              private zone: NgZone,
              private elementRef: ElementRef,
              private overlay: Overlay,
              private beyondTabsService: BeyondTabsService,
              private cdr: ChangeDetectorRef,
              private viewContainerRef: ViewContainerRef,
              private fieldDebugService: FieldDebugService) {
  }

  ngOnInit(): void {
    this.fieldDebugService.enabled$
      .pipe(untilDestroyed(this))
      .subscribe(enabled => {
        this.fieldDebugEnabled = enabled;
        if (this.dataGrid?.instance) {
          this.dataGrid.instance.refresh();
        }
        if (!enabled) {
          this.itemsTableHeaderDebugService.closeAndRemoveColumnDebugIndicator(this.elementRef);
        }
      });

    this.hasAddPermission = this.permissionsService.hasPermission(
      this.objectDefinition?.name,
      [PermissionActionEnum.ADD, PermissionActionEnum.FULL]
    );

    this.filterFormControl = new FormControl<string>('');
    this.filterFormControl.valueChanges
      .pipe(
        untilDestroyed(this),
        debounceTime(DEBOUNCE_TIME),
        distinctUntilChanged()
      )
      .subscribe((val: string) => {
        if (!this.detailTable) {
          this.paginationChanged({filter: val});
          return;
        }
        if (this.datagridExist) {
          this.dataGrid.instance.searchByText(val);
        } else {
          this.applyFilterItems();
        }
      });

    this.lookupInputChanged$
      .pipe(
        untilDestroyed(this),
        debounceTime(DEBOUNCE_TIME)
      )
      .subscribe((cellInputEvent: CellInputEvent) => {
        this.lookupCellInput(cellInputEvent);
      });
  }


  ngAfterViewInit(): void {
    this.tableActions = new BeyondTableActions(this.overlay, this.viewContainerRef, this.actionsDropdownTemplate);
    this.observeTableResize();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.isEditMode = isEditMode(this.mode);
    this.inlineEditing = this.isEditMode && (!this.objectDefinition?.editInDialog || this.partialInlineEdit);

    if (changes.mode) {
      this.isValidation = false;
      this.conditionalFormattingFilters = [];
      if (this.mode === ModeEnum.Add) {
        this.dataGrid?.instance.clearGrouping();
        if (this.autoNewRow) {
          if (!this.items.length) {
            this.allowFocusNewRow = true;
          }
        }
      }
    }

    if (changes.title) {
      this.objectDefinition.title = this.title;
    }

    if (changes.items) {
      if (this.items) {
        if (!this.datagridExist) {
          this.applyFilterItems();
        }
        this.items
          .filter(item => !!item)
          .forEach(item => {
            if (this.noIdentifier) {
              item[DEVEXTREME_KEY_PROPERTY] = generateUUID();
            } else {
              const id = item[this.idPath] || generateUUID();
              item[DEVEXTREME_KEY_PROPERTY] = id;
            }
          });
      }
      this.dataSource = new ArrayStore({
        key: DEVEXTREME_KEY_PROPERTY,
        data: this.items,
      });
    }

    if (changes.objectDefinition || changes.groupByColumns) {
      this.prepareColumns();
      this.prepareFieldsCacheMap();
    }
  }

  private prepareColumns(): void {
    this.columns = objectColumns(this.objectDefinition, this.translate);
    this.columns = groupColumns(this.columns, this.groupByColumns);
    if (this.showColumnsSum) {
      this.getPredefinedSummaryFilters();
    }
  }

  getPredefinedSummaryFilters(): void {
    this.summaryFilters = this.columns
      .filter(c => c.dataType === 'number')
      .map(c => ({
        columnType: DevExtremeFieldTypesEnum.NUMBER,
        columnName: c.dataField,
        summaryType: SummaryTypeEnum.SUM,
        summaryFormat: c.format,
      }) as SummaryFilterModel);
  }

  private prepareFieldsCacheMap(): void {
    this.fieldsCacheMap.clear();
    if (this.objectDefinition?.fields) {
      this.objectDefinition.fields
        .forEach(field => {
          this.fieldsCacheMap.set(field.dbName, field);
        });
    }
  }

  applyFilterItems(): void{
    if (!this.items) {
      return;
    }
    const val = this.filterFormControl?.value;
    if (!val) {
      this.filteredItems = [...this.items];
    } else {
      this.filteredItems = this.items
        .filter(item =>
          Object.keys(item).some(key => {
            const field = this.objectDefinition.fields.find(f => f.dbName === key);
            const itemValue: string = field?.type === CustomFieldTypesEnum.LOOKUP ?
              this.recordDisplayValuePipe.transform(item[key], field.lookupObject) : String(item[key]).toLowerCase();
            return itemValue.includes(val.toLowerCase());
          })
        );
    }
  }


  actionClick(item: ItemActionModel): void {
    this.itemActionEvent.emit({action: item.action});
  }

  private lookupCellInput(cellInputEvent: CellInputEvent): void {
    // if (cellInputEvent.value === '') {
    //   this.setCellValue(cellInputEvent.rowIndex, cellInputEvent.dataField, null);
    // }
    const field = cellInputEvent.field;
    const lookupCellFieldFilter = this.prepareCompositeFilter(cellInputEvent);
    const compositeFilter = mergeCompositeFilters(lookupCellFieldFilter, field.filters);
    ObjectInDialogComponent.open(this.matDialog, {
      parent: this.parent,
      objectName: field.lookupObject.name,
      compositeFilter,
      standalone: true,
      chooser: true,
    })
      .afterClosed()
      .subscribe(val => {
        if (!val) {
          if (this.lastCellData?.value) {
            this.setCellValue(this.lastCellData.rowIndex, this.lastCellData.dataField, this.lastCellData.value);
          }
          this.itemsTableLookupColumnHelper.focusNextEditableCell(cellInputEvent.rowIndex, cellInputEvent.columnIndex);
          return;
        }
        const row: DxDataGridTypes.Row = getRowFromVisibleRows(this.dataGrid.instance, cellInputEvent.key);
        this.setCellValue(row.rowIndex, cellInputEvent.dataField, val);
        //this.dataGrid.instance.saveEditData();
        if (this.inlineEditing) {
          this.itemsTableLookupColumnHelper.autoAddNewRowFunction(row, row.rowIndex, cellInputEvent.dataField, this);
        }
      });
  }

  private prepareCompositeFilter(cellInputEvent: CellInputEvent): ObjectCompositeFilterModel {
    if (!!cellInputEvent.value && cellInputEvent.value.length) {
      let path: string = pathSkipFirst(cellInputEvent.field.path);
      let fieldName: string = null;
      // Use first field if multiple fields are specified
      const mainFieldPaths = splitFieldPaths(cellInputEvent.field.lookupObject.mainField);
      const firstMainField = mainFieldPaths[0];
      if (isPath(firstMainField)) {
        path = capitalizePath(`${path}.${pathSkipLast(firstMainField)}`);
        fieldName = pathGetLast(firstMainField);
      } else {
        fieldName = firstMainField;
      }
      const filterItem = createFieldFilter('item1', path, fieldName, cellInputEvent.value);
      return {
        filters: [filterItem],
        expression: 'item1',
      };
    }
    return null;
  }

  focusCell(rowIndex: number, columnIndex: number, dataGrid: DxDataGrid): void {
    const el = dataGrid.getCellElement(rowIndex, columnIndex);
    dataGrid.focus(el);
    dataGrid.editCell(rowIndex, columnIndex);
  }

  onCellPrepared(e: CellPreparedEvent): void {
    this.selectAllCheckBox(e);

    if (this.viewActions && this.itemOptions && e.rowType === 'data' && e.column.type === 'buttons' && (e.column as any).command === 'edit') {
      this.tableActions.init(e);
    }

    if (e.rowType === 'data' && !!e.column.dataField) {
      const inputElement = e.cellElement.firstChild;
      if (inputElement && this.inlineEditing) {
        const field = this.findFieldByDbName(e.column.dataField);
        if (this.isValidation && !e.row.data[NEW__]) {
          setTimeout(() => {
            this.validateSingleCell(e, field);
          });
        }

        inputElement.addEventListener('focusout', () => {
          this.itemsTableLookupColumnHelper.closeDropdown();
        });

        inputElement.addEventListener('change', (event) => {
          const rowIndex: number = e.row.rowIndex;
          const dataField: string = e.column.dataField;
          const value: string = (event.target as any).value;
          const rowObj = e.row;
          if (field?.type === CustomFieldTypesEnum.LOOKUP) {
            this.fixLookupValue(value, dataField, rowIndex);
          }
          if (field?.type !== CustomFieldTypesEnum.LOOKUP) {
            const row: DxDataGridTypes.Row  = getRowFromVisibleRows(this.dataGrid.instance, e.key);
            this.itemsTableLookupColumnHelper.autoAddNewRowFunction(row, row.rowIndex, field.dbName, this);
          }
        });

        if (field?.type === CustomFieldTypesEnum.LOOKUP) {
          inputElement.addEventListener('focusin', () => {
            this.itemsTableLookupColumnHelper.closeDropdown();
            const row: DxDataGridTypes.Row = getRowFromVisibleRows(this.dataGrid.instance, e.key);
            this.lastCellData = {
              dataField: e.column.dataField,
              rowIndex: row.rowIndex,
              columnIndex: e.columnIndex,
              value: e.data[e.column.dataField],
            };
          });

          inputElement.addEventListener('keydown', (keydownEvent: KeyboardEvent) => {
            if (keydownEvent.keyCode === F6_KEY_CODE) {
              const columnIndex: number = e.columnIndex;
              const dataField: string = e.column.dataField;
              const key: string = e.row.key;
              const rowIndex: number = e.row.rowIndex;
              this.openLookupDialog(columnIndex, dataField, key, rowIndex);
            }
            this.itemsTableLookupColumnHelper.onKeyDown(keydownEvent, field, e, this.dataGrid.instance);
          });

          this.itemsTableLookupColumnHelper.addOverlayOrigin(e, field);
          this.itemsTableLookupColumnHelper.addLookupButton(e, field);
        }
      }

      if ((e.column as ColumnType)?.customColor) {
        e.cellElement.style.color = (e.column as ColumnType).customColor;
      }

      if (this.conditionalFormattingFilters?.length) {
        const cellObject = this.createDataGridCellObjModel(e);
        generateConditionalFormatting(cellObject, this.conditionalFormattingFilters);
      }

      if (!this.inlineEditing) {
        const field = this.findFieldByDbName(e.column.dataField);
        if (field?.type === CustomFieldTypesEnum.LOOKUP) {
          e.cellElement.classList.add('cell-link-highlight');
        }
      }
    }

    if (e.rowType === 'data' && this.disableRowFn?.(e.data)) {
      const field = this.findFieldByDbName(e.column.dataField);
      if (field?.type !== CustomFieldTypesEnum.LOOKUP) {
        e.cellElement.style.pointerEvents = 'none';
        e.cellElement.style.opacity = '0.5';
      }
    }

    if (e.rowType === 'header') {
      if (!this.showSelectAll && e.cellElement.classList.contains('dx-command-select')) {
        e.cellElement.remove();
      }
      this.itemsTableHeaderPopupService.addFixColumnPopup(e, this.columnFixPopup, this.viewContainerRef);

      const field = this.findFieldByDbName(e.column.dataField);
      if (field?.mandatory && this.detailTable && this.inlineEditing) {
        this.itemsTableHeaderPopupService.addAsterixIcon(e);
      }

      this.itemsTableHeaderDebugService.addColumnDebugIndicator(
        e,
        this.columnDebugPopup,
        this.viewContainerRef,
        this.fieldsCacheMap.get(e.column.dataField),
        this.fieldDebugEnabled
      );
    }

    if (e.rowType === 'data' && !!e.column.dataField) {
      this.cellValueShowAsChip(e);
    }
  }


  private cellValueShowAsChip(e: CellPreparedEvent): void {
    const field = this.fieldsCacheMap.get(e.column.dataField);
    if (field?.showAsBadge) {
      this.showValueAsChip(e);
    }
  }

  showValueAsChip(e: CellPreparedEvent): void {
    const cellValue = e.value;
    if (!cellValue) {
      return;
    }

    const childElement = document.createElement('div');
    e.cellElement.innerHTML = '';
    childElement.innerText = e.text;
    childElement.classList.add('status-color-class');
    childElement.classList.add(`status-color-class--${cellValue}`);
    e.cellElement.appendChild(childElement);
  }

  onKeyDown(e: any): void {
    if (this.inlineEditing) {
      if (e.event.originalEvent?.keyCode === HOME) {
        e.event.originalEvent.preventDefault();
        this.navigateToRow(e.component, true);
      }
      if (e.event.originalEvent?.keyCode === END) {
        e.event.originalEvent.preventDefault();
        this.navigateToRow(e.component, false);
      }

      if (e.event.originalEvent?.keyCode === DELETE) {
        const selectedRows = e.component.getSelectedRowsData();
        if (!selectedRows.length) {
          return;
        }
        e.event.originalEvent?.preventDefault();
        this.deleteSelectedItems();
      }
    }
  }

  private navigateToRow(dataGrid: DxDataGrid, first: boolean): void {
    const allRows = this.items;
    if (!allRows.length) {
      return;
    }

    if (first) {
      dataGrid.getScrollable().scrollTo(0);
    } else {
      const distance = dataGrid.getScrollable().scrollHeight();
      dataGrid.getScrollable().scrollBy(distance);
    }
    setTimeout(() => {
      if (!first) {
        dataGrid.getScrollable().scrollBy(1000);
      }
      const visibleRows = dataGrid.getVisibleRows();
      const rowIndex = first ? 0 : visibleRows.length - 1;
      const columnIndex: number = dataGrid.getVisibleColumns().findIndex(c => !!c.dataField && c.allowEditing);
      dataGrid.editCell(rowIndex, columnIndex);
    });
  }

  private validateSingleCell(e: CellPreparedEvent, field: CustomFieldModel): void {
    const valid = this.itemsTableValidation.validateSingleCell(e);
    if (!valid) {
      this.addInvalidCellClass({rowIndex: e.rowIndex, dataField: field.dbName});
    }
  }

  private selectAllCheckBox(e: CellPreparedEvent): void {
    if (e.rowType === 'header' && e.column.type === 'selection') {
      this.handleHeaderSelectAll(e);
    }
  }

  private handleHeaderSelectAll(e: CellPreparedEvent): void {
    const selectAllCheckBox = e.cellElement.querySelector('.dx-select-checkbox');
    const hasDisabledRow = this.items?.some(item => this.disableRowFn?.(item));

    if (selectAllCheckBox && hasDisabledRow) {
      selectAllCheckBox.addEventListener('click', (event: MouseEvent) => {
        event.stopImmediatePropagation();
        this.toggleRowSelection(e);
      });
    }
  }

  private toggleRowSelection(e: CellPreparedEvent): void {
    const gridInstance = e.component;
    const selectedRowsKeys = gridInstance.getSelectedRowKeys();

    const validRowsKeys = this.items
      .filter(item => !this.disableRowFn?.(item))
      .map(item => item[DEVEXTREME_KEY_PROPERTY]);

    if (selectedRowsKeys?.length === validRowsKeys?.length) {
      gridInstance.deselectAll();
    } else {
      gridInstance.selectRows(validRowsKeys, false);
    }
  }

  private fixLookupValue(value: string, dataField: string, rowIndex: number): void {
    const field = this.findFieldByDbName(dataField);
    if (!!field?.lookupObject) {
      if (this.lastCellData.dataField === field.dbName && this.lastCellData.rowIndex === rowIndex) {
        if (value === '' || typeof value !== 'object') {
          this.setCellValue(rowIndex, dataField, null);
          this.lastCellData.value = null;
        }
      }
    }
  }

  openLookupDialog(columnIndex: number, dataField: string, key: string, rowIndex: number, value?: string): void {
    const field = this.findFieldByDbName(dataField);
    if (!!field?.lookupObject) {
      this.lookupInputChanged$.next({key, dataField, field, rowIndex, columnIndex, value: value || ''});
    }
  }

  findFieldByDbName(name: string): CustomFieldModel {
    const fieldFromCache = this.fieldsCacheMap.get(name);
    if (fieldFromCache) {
      return fieldFromCache;
    }
    return this.objectDefinition.fields?.find(f => f.dbName === name);
  }

  insert(item: T): void {
    item[NEW__] = false;
    if (this.datagridExist) {
      this.dataSource.insert(item);
      this.dataGrid.instance.refresh();
    } else {
      this.items.push(item);
      this.applyFilterItems();
    }
    setTimeout(() => {
      this.itemActionEvent.emit({action: ItemActionEnum.Added, data: item});
    });
  }

  update(key: any, item: T): void {
    item[UPDATED__] = true;
    if (this.datagridExist) {
      this.dataSource.update(key, item);
      this.dataGrid.instance.refresh();
    } else {
      const index = this.items.findIndex(i => i[DEVEXTREME_KEY_PROPERTY] === key);
      if (index > -1) {
        this.items[index] = item;
        this.applyFilterItems();
      }
    }
    setTimeout(() => {
      this.itemActionEvent.emit({action: ItemActionEnum.Edited, data: item});
    });
  }

  filter(text: string): void {
    this.dataGrid.instance.searchByText(text);
  }

  getSelectedItems(): T[] {
    return this.dataGrid.instance.getSelectedRowsData();
  }

  selectAllItems(): void {
    this.dataGrid.instance.selectAll();
  }

  selectItemsByIndexes(indexes: number[]): void {
    this.dataGrid.instance.selectRowsByIndexes(indexes);
  }

  selectItemsByIds(ids: number[], preserve: boolean): void {
    this.dataGrid.instance.selectRows(ids, preserve);
  }

  deselectItemsByIds(ids: number[]): void {
    this.dataGrid.instance.deselectRows(ids);
  }

  deselectAllItems(): void {
    this.dataGrid.instance.deselectAll();
  }

  setCellValue(rowIndex: number, dataField: string, val: any): void {
    if (!this.items[rowIndex]) {
      return;
    }
    const rowData = this.items[rowIndex];
    rowData[UPDATED__] = true;
    this.dataGrid.instance.cellValue(rowIndex, dataField, val);
    this.lastEditedDataField = dataField;
  }

  onEditingStart(event: any): void {
    const data: ItemEditingCellStartModel = {dataField: event.column.dataField, item: event.data};
    const action: ItemActionParamsModel = {action: ItemActionEnum.EditingCellStart, data};

    if (event?.column?.dataField != null
      && this.partialInlineEditColumns
      && !this.partialInlineEditColumns.some(c => c === event.column.dataField)) {
      return;
    }

    this.itemActionEvent.emit(action);
    if (action.cancel) {
      event.cancel = action.cancel;
    }
  }

  onEditingEnd(event: any): void {
    const row: DxDataGridTypes.Row = getRowFromVisibleRows(this.dataGrid.instance, event.key);
    if (!row) {
      return;
    }
    const index = (row as any).dataIndex;
    const rowData = (row as any).data;
    rowData[UPDATED__] = true;
    const actionData: ItemEditingCellEndModel = {
      dataField: this.lastEditedDataField,
      item: rowData,
      repaintRow: this.repaintRowFn(index, rowData),
    };
    this.itemActionEvent.emit({action: ItemActionEnum.EditingCellEnd, data: actionData});
  }

  onEditorPreparing(event: any): void {
    if (event.parentType === 'dataRow') {
      event.editorOptions.onValueChanged = (args: any) => {
        this.lastEditedDataField = event.dataField;
        event.setValue(args.value);
        const field = this.findFieldByDbName(event.dataField);
        if (field && (field.type === CustomFieldTypesEnum.DATE || field.type === CustomFieldTypesEnum.DATE_TIME)) {
          this.dataGrid.instance.closeEditCell();
        }
      };
    }
  }

  private repaintRowFn = (index: number, rowData: any) => {
    return (actionResponse: ActionResponseModel) => {
      Object.assign(rowData, actionResponse.response);
      this.dataGrid.instance.repaintRows([index]);
    };
  };

  onRowClick(rowType: string, rowIndex: number, key: any, columnIndex: number, dataField: string, item: any): void {

    const data: ItemActionParamsModel = {
      action: ItemActionEnum.SelectedItem,
      data: {
        data: item,
        rowIndex: rowIndex,
        columnName: dataField,
      } as ItemTableRowClickModel,
    };
    this.itemActionEvent.emit(data);

    if (!this.detailTable) {
      return;
    }

    if (!this.objectDefinition.editInDialog && !this.partialInlineEdit) {
      return;
    }

    if (this.partialInlineEdit && this.mode !== ModeEnum.View && this.partialInlineEditColumns.some(c => c === dataField)) {
      return;
    }

    if (rowType !== 'data' || this.preventRowClick || data.cancel) {
      return;
    }
    const id = valueOnPath(item, this.idPath);
    if (this.standalone) {
      this.openInDialog({key, id, data: null, standalone: this.standalone}, this.objectDefinition.name, null, ModeEnum.View);
    } else {
      const mode = this.mode === ModeEnum.View ? ModeEnum.View : ModeEnum.Edit;
      this.openInDialog({key, id, data: item, standalone: this.standalone }, null, this.objectDefinition, mode);
    }
  }

  onRowRemoved(event: any): void {
    if (this.incrementField) {
      this.recalculateRbr();
    }
    this.itemsToDelete([event.data]);
    setTimeout(() => {
      this.itemActionEvent.emit({action: ItemActionEnum.Deleted, data: event.data});
    });
  }

  onRowRemoving(event: any): void {
    const data: ItemActionParamsModel = {action: ItemActionEnum.Delete, data: event.data};
    this.itemActionEvent.emit(data);
    if (data.cancel) {
      event.cancel = data.cancel;
    }
  }

  onCellClick(event: CellClickEvent): void {
    const field: CustomFieldModel = this.findFieldByDbName(event.column.dataField);
    if (event.rowType === 'data' && field?.type === CustomFieldTypesEnum.LOOKUP && !this.inlineEditing) {
      if (this.isTextContentClick(event) && !field?.preventLinkNavigation) {
        this.navigate(event, field);
      }
    }

    this.onRowClick(event.row.rowType, event.row.rowIndex, event.key, event.columnIndex, event.column.dataField, event.data);
  }

  onItemsCardClick(event: ItemActionParamsModel): void {
    if (event.action === ItemActionEnum.Delete) {
      const index = this.items.findIndex(data => data.id === event.data.id);
      if (index > -1) {
        this.items.splice(index, 1);
        this.applyFilterItems();
        this.itemActionEvent.emit(event);
      }
    } else {
      const data: ItemActionParamsModel = {
        action: ItemActionEnum.SelectedItem,
        data: {
          data: event.data,
        } as ItemTableRowClickModel,
      };
      this.itemActionEvent.emit(data);

      if (!this.detailTable) {
        return;
      }

      const id = valueOnPath(data, this.idPath);
      this.openInDialog({key: event.data[DEVEXTREME_KEY_PROPERTY], id, data: event.data, standalone: false}, this.objectDefinition.name, this.objectDefinition, this.mode);
    }
  }

  navigate(e: CellClickEvent, field: CustomFieldModel): void {
    const value = valueOnPath(e.data, field.dbName);
    if (value && value.id) {
      if (this.openLookupFn) {
        this.openLookupFn(field.lookupObject.name as ObjectNameEnum, value?.id);
      } else {
        this.beyondTabsService.addTab({
          objectName: field.lookupObject.name as ObjectNameEnum,
          id: value?.id,
        });
      }
      e.event.stopImmediatePropagation();
    }
  }

  isTextContentClick(e: CellClickEvent): boolean {
    const inputElement = e.cellElement.firstChild as HTMLElement;

    if (inputElement && inputElement.textContent) {
      const range = document.createRange();
      range.selectNodeContents(inputElement);
      const rect: DOMRect = range.getClientRects()[0];

      const clickX = e.event.clientX;
      const clickY = e.event.clientY;
      if (clickX >= rect.left && clickX <= rect.right && clickY >= rect.top && clickY <= rect.bottom) {
        return true;
      }
    }
    return false;
  }

  recalculateRbr(): void {
    let counter = 1;
    for (const item of this.items) {
      item[this.incrementField] = counter++;
    }
  }

  addNewClick(): void {
    if (this.disableAddItemFn) {
      const isHeaderFormInvalid = this.disableAddItemFn();
      if (isHeaderFormInvalid) {
        return;
      }
    }

    const data = {} as T;
    this.prepareNewRow(data);
    if (this.handleAddAction) {
      this.itemActionEvent.emit({action: ItemActionEnum.Add});
      return;
    }
    if (!this.standalone) {
      this.openInDialog({data, id: null, standalone: this.standalone}, null, this.objectDefinition, ModeEnum.Add);
    } else if (this.objectDefinition.editInDialog && !this.partialInlineEdit) {
      this.insertFromDialog(data);
    } else {
      if (this.datagridExist) {
        this.allowFocusNewRow = true;
        this.addNew(this.dataGrid.instance);
      } else {
        this.openInDialog({data, id: null, standalone: false}, null, this.objectDefinition, ModeEnum.Add);
      }
    }
  }

  onInitNewRow(event: any): void {
    this.prepareNewRow(event.data);
  }

  private prepareNewRow(data: any): void {
    data[DEVEXTREME_KEY_PROPERTY] = generateUUID();
    if (this.incrementField) {
      data[this.incrementField] = this.items?.length != null ? this.items.length + 1 : 1;
    }
    if (this.inlineEditing && this.mode === ModeEnum.Add) {
      data[NEW__] = true;
    }
    data.korisnik = this.userContext.username;
    data.datumPromene = now();
    if (this.initNewItemFn) {
      this.initNewItemFn(data);
    } else {
      console.warn('initNewRowFn not defined');
    }
  }

  onInitialized(event: InitializedEvent): void {
    if (this.inlineEditing && this.autoNewRow && this.mode === ModeEnum.Add) {
      this.addNew(event.component);
    }
  }

  private addNew(dataGrid: DxDataGrid, cellInfo?: { rowIndex: number; columnIndex: number }): void {
    if (this.mode === ModeEnum.Edit) {
      const distance = dataGrid.getScrollable().scrollHeight();
      dataGrid.getScrollable().scrollBy(distance + 10);
    }
    const data = {} as T;
    this.prepareNewRow(data);
    this.insertNewRow(data, dataGrid, cellInfo);
  }

  private insertNewRow(item: T, dataGrid: any, cellInfo?: { rowIndex: number; columnIndex: number }): void {
    this.dataSource.insert(item);
    dataGrid.refresh();
    this.setRowFocus(dataGrid, item , cellInfo);
    setTimeout(() => {
      this.itemActionEvent.emit({action: ItemActionEnum.Added, data: item});
    });
  }

  private setRowFocus(dataGrid: DxDataGrid, item: any, cellInfo?: {
    rowIndex: number;
    columnIndex: number;
  }): void {
    setTimeout(() => {
      if (this.allowFocusNewRow) {
        const rowIndex = !this.items.length ? 0 : getRowFromVisibleRows(dataGrid, item[DEVEXTREME_KEY_PROPERTY]).rowIndex;
        const columnIndex = getFirstEditableColumn(dataGrid);
        this.focusCell(rowIndex, columnIndex, dataGrid);
        this.allowFocusNewRow = false;
      } else {
        this.focusCell(cellInfo.rowIndex, cellInfo.columnIndex, dataGrid);
      }
    }, 300);
  }

  addNewBlankRow(cellInfo?: { rowIndex: number; columnIndex: number }): void {
    if (this.inlineEditing) {
      this.addNew(this.dataGrid.instance, cellInfo);
    }
  }

  private insertFromDialog(dataRow: any): void {
    const data: ObjectInDialogData = {
      parent: this.parent,
      objectDefinition: this.objectDefinition,
      standalone: true,
      chooser: this.chooserForNew,
      data: dataRow,
      mode: this.chooserForNew ? undefined : ModeEnum.Add,
    };
    ObjectInDialogComponent.open<T>(this.matDialog, data)
      .afterClosed()
      .subscribe((res: T) => {
        if (res) {
          this.insert(res);
          if (this.createAnotherItem) {
            this.addNewClick();
          }
        }
      });
  }

  get datagridExist(): boolean {
    return !!this.dataGrid?.instance;
  }

  private openInDialog(payload: {
    key?: any;
    id: any;
    data?: T;
    standalone: boolean;
  }, objectName: string, objectDefinition: ObjectDefinitionModel, mode?: ModeEnum): void {
    const data: ObjectInDialogData = {
      parent: this.parent,
      objectName: objectName,
      objectDefinition: objectDefinition,
      standalone: payload.standalone,
      chooser: false,
      dataId: payload.id,
      data: payload.data,
      mode: mode,
    };
    ObjectInDialogComponent.open<T>(this.matDialog, data)
      .afterClosed()
      .pipe(filter(res => !!res))
      .subscribe((res: T) => {
        if (!payload.key && payload.key !== 0) {
          this.insert(res);
          if (this.createAnotherItem) {
            this.addNewClick();
          }
        } else {
          this.update(payload.key, res);
        }
      });
  }

  public onRowPrepared(e: any): void {
    e.rowElement.style.height = '40px';
  }

  openSettingsDialog(): void {
    const objectRestService = this.objectRestServiceCreator.create(this.objectDefinition.name);
    FieldSettingsDialogComponent.open(this.matDialog, {object: this.objectDefinition})
      .afterClosed()
      .pipe(
        switchMap((res) => {
          if (res) {
            return objectRestService.saveColumns({fields: res.userFieldSettings, setForAll: res.setForAll});
          } else {
            return of(null);
          }
        })
      )
      .subscribe(objectDefinition => {
        if (objectDefinition) {
          this.objectDefinition.columns = objectDefinition.columns;
          this.objectDefinition.fields
            .forEach(f => f.visible = objectDefinition.columns.some(c => c === f.dbName));
          this.objectDefinition.filters
            .forEach(f => f.visible = objectDefinition.columns.some(c => c === f.dbName));
          this.prepareColumns();
        }
      });
  }

  onFilterItemChanged(change: FilterItemChangedModel): void {
    this.itemActionEvent.emit({action: ItemActionEnum.FilterItemChanged, data: change});
  }

  onApplyFilters(filters: ObjectCompositeFilterModel): void {
    this.itemActionEvent.emit({
      action: ItemActionEnum.LoadItems,
      data: {
        compositeFilter: filters,
        saveFilters: filters?.chosenFiltersSaved,
      } as LoadItemsSettingsModel,
    });
  }

  setActiveFiltersHeight(value: ActiveFiltersHeightTypeEnum): void {
    this.activeFiltersHeight = value;
  }

  exportData(): void {
    const objectNamePipe = new ObjectNamePipe(this.translate);
    const name = objectNamePipe.transform(this.objectDefinition);
    exportTableData(this.dataGrid.instance, name);
  }

  tabChanged(event: { index: number }): void {
    const tab = this.tabs[event.index];
    this.itemActionEvent.emit({action: ItemActionEnum.TabChanged, data: tab});
  }

  openFormatDialog(): void {
    FormattingDialogComponent.open(this.matDialog, {
      columns: this.dataGrid.instance.getVisibleColumns().filter(c => c.dataField) as ColumnModel[],
      fieldFilters: this.conditionalFormattingFilters,
      fields: this.objectDefinition.fields,
    })
      .afterClosed()
      .subscribe((result: DataGridObjectFilterModel[]) => {
        this.conditionalFormattingFilters = result;
        this.dataGrid.instance.repaint();
      });
  }

  openSummariesDialog(): void {
    SummariesDialogComponent.open(this.matDialog, {
      columns: this.dataGrid.instance.getVisibleColumns().filter(c => c.dataField) as ColumnModel[],
      summaryFilters: this.summaryFilters,
      fields: this.objectDefinition.fields,
    })
      .afterClosed()
      .subscribe((result: SummaryFilterModel[]) => {
        this.summaryFilters = result;
        this.summaryFilters.forEach(s => {
          if (s.summaryType !== SummaryTypeEnum.COUNT) {
            s.summaryFormat = this.columns.find(c => c.dataField === s.columnName)?.format;
          }
        });
      });
  }

  displayFormat(summaryType: SummaryTypeEnum): string {
    const textSummaryType = summaryType?.charAt(0).toUpperCase() + summaryType?.slice(1);
    return `${textSummaryType}: {0}`;
  }

  calculateSelectedRow = (options: Parameters<DxDataGridTypes.Summary['calculateCustomSummary']>[0]) => {
    const column = options.name;
    const summaryType = this.summaryFilters.find(s => s.columnName === column)?.summaryType;

    if (options.summaryProcess === 'start') {
      options.totalValue = 0;
      (options as any).selectedTotalValue = 0;
      (options as any).count = 0;
      (options as any).selectedCount = 0;
      if (summaryType === SummaryTypeEnum.MIN || summaryType === SummaryTypeEnum.MAX) {
        options.totalValue = null;
        (options as any).selectedTotalValue = null;
      }
    }

    if (options.value && options.summaryProcess === 'calculate') {
      const isRowSelected = options.value && options.component.isRowSelected(options.value[DEVEXTREME_KEY_PROPERTY]);

      const field = this.fieldsCacheMap.get(column);
      const value = valueOnPath(options.value, column);

      if (value != null && value !== '') {
        (options as any).count++;
        if (isRowSelected) {
          (options as any).selectedCount++;
        }

        switch (summaryType) {
          case SummaryTypeEnum.SUM:
          case SummaryTypeEnum.AVG:
            options.totalValue += value;
            if (isRowSelected) {
              (options as any).selectedTotalValue += value;
            }
            break;
          case SummaryTypeEnum.MIN:
            options.totalValue = !options.totalValue ? value : this.min(field.type, options.totalValue, value);
            if (isRowSelected) {
              (options as any).selectedTotalValue = !(options as any).selectedTotalValue ? value : this.min(field.type, (options as any).selectedTotalValue, value);
            }
            break;
          case SummaryTypeEnum.MAX:
            options.totalValue = !options.totalValue ? value : this.max(field.type, options.totalValue, value);
            if (isRowSelected) {
              (options as any).selectedTotalValue = !(options as any).selectedTotalValue ? value : this.max(field.type, (options as any).selectedTotalValue, value);
            }
            break;
        }
      }
    }

    if (options.summaryProcess === 'finalize') {
      if (summaryType === SummaryTypeEnum.SUM) {
        if ((options as any).selectedCount > 0) {
          options.totalValue = (options as any).selectedTotalValue;
        }
      }
      if (summaryType === SummaryTypeEnum.AVG) {
        options.totalValue = round(options.totalValue / (options as any).count);
        if ((options as any).selectedCount > 0) {
          options.totalValue = round((options as any).selectedTotalValue / (options as any).selectedCount);
        }
      }
      if (summaryType === SummaryTypeEnum.COUNT) {
        options.totalValue = (options as any).selectedCount > 0 ? (options as any).selectedCount : (options as any).count;
      }
      if (summaryType === SummaryTypeEnum.MIN || summaryType === SummaryTypeEnum.MAX) {
        if ((options as any).selectedCount > 0) {
          options.totalValue = (options as any).selectedTotalValue;
        }
      }
    }
  };

  private min(type: CustomFieldTypesEnum, val1: any, val2: any): number | Date {
    if (type === CustomFieldTypesEnum.NUMBER || type === CustomFieldTypesEnum.DECIMAL_NUMBER) {
      return Math.min(val1, val2);
    } else if (type === CustomFieldTypesEnum.DATE || type === CustomFieldTypesEnum.DATE_TIME) {
      const date1 = val1 instanceof Date ? val1 : new Date(val1);
      const date2 = val2 instanceof Date ? val2 : new Date(val2);
      return date1 > date2 ? date2 : date1;
    }
    return null;
  }

  private max(type: CustomFieldTypesEnum, val1: any, val2: any): number | Date {
    if (type === CustomFieldTypesEnum.NUMBER || type === CustomFieldTypesEnum.DECIMAL_NUMBER) {
      return Math.max(val1, val2);
    } else if (type === CustomFieldTypesEnum.DATE || type === CustomFieldTypesEnum.DATE_TIME) {
      const date1 = val1 instanceof Date ? val1 : new Date(val1);
      const date2 = val2 instanceof Date ? val2 : new Date(val2);
      return date1 < date2 ? date2 : date1;
    }
    return null;
  }

  createDataGridCellObjModel(e: CellPreparedEvent): DataGridCellObjectModel {
    return {
      rowType: e.rowType,
      column: {
        caption: e.column.caption,
        dataField: e.column?.dataField,
        dataType: e.column.dataType,
      },
      data: e.data,
      cellElement: e.cellElement,
      value: e.value,
    };
  }

  onOptionChanged(e: OptionChangedEvent): void {
    const actionName = e.fullName.split('.')[1];
    if (!this.preventSaveColumnChanges) {
      if (e.name === 'columns' && actionName === 'width') {
        this.lastEvent = e;
        this.columnResized = true;
        this.draggedColumn = null;
      }
      if (e.name === 'columns' && actionName === 'visibleIndex') {
        if (this.objectDefinition) {
          saveChangedColumnReordering(this.columns, e, this.objectRestServiceCreator, this.objectDefinition);
          this.lastEvent = e;
        }
      }
    }
    if (e.name === 'columns' && actionName === 'filterValues' || e.name === 'columns' && actionName === 'filterValue') {
      this.addColumnHeaderStyle(e, actionName);
    }

    if (!this.detailTable) {
      if (e.name === 'columns' && actionName === 'sortOrder') {
        const visibleColumns = this.dataGrid.instance.getVisibleColumns();
        const sortedColumns = visibleColumns
          .filter(col => col.sortOrder)
          .sort((a, b) => a.sortIndex - b.sortIndex);
        const sortArray: string[] = sortedColumns.map(col => `${col.dataField},${col.sortOrder}`);
        if (sortArray.length) {
          this.paginationChanged({sort: sortArray});
        }
      }
    }
  }

  private paginationChanged(pagination: Partial<BeyondPaginationModel>): void {
    this.paginationChangedEvent.next({
      ...this.pagination,
      ...pagination,
    });
  }

  addColumnHeaderStyle(e: OptionChangedEvent, actionName: string): void {
    const columnIndex: number = getColumnNumber(e.fullName);
    const column = this.dataGrid.instance.getVisibleColumns().find(c => c.visibleIndex === columnIndex) as ColumnModel;
    const columnHeader = this.elementRef.nativeElement.querySelector(`.dx-header-row .dx-datagrid-action[aria-label*="${column.caption}"]`) as HTMLElement;
    handleColumnFilterValues(column, actionName, columnHeader);
  }

  onSelectionChanged(event: SelectionChangedEvent): void {
    this.hasSelectedRows = !!event.selectedRowsData?.length;
    const data: ItemsTableSelectionChangeModel = {
      currentSelectedRowKeys: event.currentSelectedRowKeys,
      selectedRows: event.selectedRowsData,
    };
    this.itemActionEvent.emit({action: ItemActionEnum.SelectionChanged, data: data});

    if (this.summaryFilters.length) {
      this.dataGrid.instance.refresh().then();
    }
  }

  mouseUpEvent(e: MouseEvent): void {
    if (this.objectDefinition && this.columnResized) {
      saveChangedColumnWidth(this.columns, this.lastEvent, this.objectRestServiceCreator, this.objectDefinition);
      this.columnResized = false;
      this.lastEvent = null;
    }
  }

  onContentReady(e: any): void {
    this.columnHeaders = e.element.querySelectorAll('.dx-datagrid-drag-action');
    this.columnHeaders.forEach(header => {
      off(header, 'dxdragstart', this.onColumnDragStart);
      off(header, 'dxdragend', this.onColumnDragEnd);
      on(header, 'dxdragstart', this.onColumnDragStart);
      on(header, 'dxdragend', this.onColumnDragEnd);
      header.removeEventListener('click', this.onColumnFixClick);
      header.addEventListener('click', this.onColumnFixClick);
    });
    this.addActionColumnName();
  }

  onColumnFixClick = (event: any): void => {
    const debugIconElement = event.target.closest('.c-items-table__column-debug-icon');
    if (debugIconElement) {
      const colId = debugIconElement.getAttribute('data-column-id');
      const column = this.dataGrid.instance.getVisibleColumns().find(c => c.dataField === colId);
      const field = this.fieldsCacheMap.get(colId);
      if (!column) {
        return;
      }
      event.stopPropagation();
      this.itemsTableHeaderDebugService.open(
        debugIconElement,
        this.columnDebugPopup,
        this.viewContainerRef,
        {path: field?.dbName ?? column?.dataField, columnName: field?.columnName ?? ''}
      );
      return;
    }

    const currentColumnElement = event.target.closest('.c-items-table-header-icon-freeze');
    if (currentColumnElement) {
      const colId = currentColumnElement.getAttribute('id').split('__')[1];
      const column = this.dataGrid.instance.getVisibleColumns().find(c => c.dataField === colId);
      event.stopPropagation();
      this.itemsTableHeaderPopupService.open(event, column, this.columnFixPopup, this.viewContainerRef);
    }
  };

  addActionColumnName(): void {
    const headerRow = this.elementRef.nativeElement.querySelector('.dx-row.dx-header-row');
    if (headerRow) {
      const actionColumnHeader = headerRow.querySelector('.dx-command-edit');
      const columnName: string = this.translate.instant('COMMON.ACTIONS');
      if (actionColumnHeader) {
        actionColumnHeader.innerHTML = columnName;
      }
    }
  }

  onColumnDragStart = (event: any): void => {
    this.draggedColumn = event.currentTarget.classList.contains('dx-group-panel-item') ? null : event.currentTarget;
    if (!this.draggedColumn) {
      return;
    }
    const draggedColRect = this.draggedColumn.getBoundingClientRect();
    this.dropZoneStyle = {
      left: `${draggedColRect.left - 10}px`,
      width: `${draggedColRect.width}px`,
      top: '110px',
    };
  };

  onColumnDragEnd = (event: MouseEvent) => {
    if (!this.draggedColumn) {
      return;
    }
    const rect = this.hideDropzoneItem.nativeElement.getBoundingClientRect();
    const mouseX = event.clientX;
    const mouseY = event.clientY;
    if (mouseX >= rect.left && mouseX <= rect.right && mouseY >= rect.top && mouseY <= rect.bottom) {
      this.draggingAction('hide');
    }
    this.draggedColumn = null;
    this.lastEvent = null;
  };

  draggingAction(action: string): void {
    if (this.draggedColumn) {
      if (action === 'hide') {
        this.hideColumn();
      } else {
        this.draggedColumn = null;
      }
    }
  }

  hideColumn(): void {
    const columnIndex: number = this.lastEvent ? this.lastEvent.value : this.draggedColumn.cellIndex;
    const cols = this.dataGrid.instance.getVisibleColumns();
    const colToHideByVisibleIndex = cols.find(e => e.visibleIndex === columnIndex);
    const updatedColumn = colToHideByVisibleIndex && this.lastEvent ? colToHideByVisibleIndex.dataField : cols[columnIndex].dataField;
    this.dataGrid.instance.columnOption(updatedColumn, 'visible', false);
    if (this.objectDefinition) {
      this.objectDefinition.columns = this.objectDefinition.columns.filter(c => c !== updatedColumn);
      saveChangedColumnReordering(this.columns, null, this.objectRestServiceCreator, this.objectDefinition);
    }
    this.draggedColumn = null;
    this.lastEvent = null;
  }

  reloadColumns(): void {
    this.prepareColumns();
  }

  observeTableResize(): void {
    let multiplier = 1;

    const tableHeight = getTableHeight(this.objectDefinition?.name);
    if (this.dataGridWrapperDiv?.nativeElement && tableHeight) {
      this.dataGridWrapperDiv.nativeElement.style.height = tableHeight;
    }

    const resizeObserver = new ResizeObserver(entries => {

      const parentEl = this.dataGrid?.instance?.getScrollable()?.content()?.parentElement;
      if (!parentEl) {
        return;
      }
      parentEl.scrollTop = parentEl.scrollTop + multiplier;
      multiplier = multiplier * -1;

      if (this.objectDefinition?.name) {
        saveTableHeight(this.objectDefinition?.name, this.dataGridWrapperDiv.nativeElement.style.height);
      }
    });

    this.zone.runOutsideAngular(() => {
      if (this.dataGridWrapperDiv?.nativeElement) {
        resizeObserver.observe(this.dataGridWrapperDiv.nativeElement);
      }
    });
  }

  setWrapperHeight(): void {
    const viewportHeight = window.innerHeight;
    const topOffset = this.dataGridWrapperDiv?.nativeElement.getBoundingClientRect().top;
    const headerHeight = 38;
    if (topOffset) {
      const spaceHeight = viewportHeight - topOffset;
      const oldTableHeight = parseInt(getTableHeight(this.objectDefinition?.name), 10) || 150;
      if (spaceHeight > oldTableHeight) {
        const height = spaceHeight - headerHeight;
        this.dataGridWrapperDiv.nativeElement.style.height = `${height}px`;
        saveTableHeight(this.objectDefinition?.name, this.dataGridWrapperDiv.nativeElement.style.height);
      }
    }
  }

  refresh(): void {
    this.itemActionEvent.emit({action: ItemActionEnum.Refresh});
  }

  rerender(): void {
    this.dataGrid.instance.repaint();
    this.dataGrid.instance.refresh();
  }

  repaint(): void {
    this.dataGrid.instance.repaint();
  }

  deleteSelectedItems(): void {
    const data: DeleteConfirmationDialogData = {
      title: this.translate.instant('DELETE_CONFIRMATION_DIALOG.TITLE'),
      description: this.translate.instant('ITEMS_TABLE.DELETE_CONFIRMATION_DIALOG.DESCRIPTION', {NAME: ''}),
      yesButtonText: this.translate.instant('DELETE_CONFIRMATION_DIALOG.YES_BUTTON'),
      noButtonText: this.translate.instant('DELETE_CONFIRMATION_DIALOG.NO_BUTTON'),
    };

    DeleteConfirmationDialogComponent.open(this.matDialog, data)
      .afterClosed()
      .subscribe(confirmed => {
        if (confirmed) {
          const rowsData =  this.datagridExist ? this.getSelectedItems() : this.getCheckboxItemsCards();
          this.deleteItems(rowsData);
        }
      });
  }

  getCheckboxItemsCards(): any[] {
    return this.cardsComponent.getCheckedItems();
  }

  deleteItems(rowsData?: any[]): void {
    if (!rowsData.length) {
      return;
    }
    rowsData
      .map(row => row[DEVEXTREME_KEY_PROPERTY])
      .forEach(id => {
        const index = this.items.findIndex(data => data[DEVEXTREME_KEY_PROPERTY] === id);
        if (index !== -1) {
          this.items.splice(index, 1);
        }
      });
    if (this.datagridExist) {
      this.dataGrid.instance.repaint();
      this.dataGrid.instance.refresh();
    } else {
      this.applyFilterItems();
    }
    this.itemsToDelete(rowsData);
    this.itemActionEvent.emit({action: ItemActionEnum.Delete, data: rowsData});
  }

  private itemsToDelete(rowsData: any[]): void {
    if (this.deleted__) {
      if (!this.deleted__[this.objectDefinition.name]) {
        this.deleted__[this.objectDefinition.name] = [];
      }
      for (const rowData of rowsData) {
        if (rowData.id) {
          this.deleted__[this.objectDefinition.name].push(rowData);
        }
      }
    }
  }

  onRowReorder = (e) => {
    const visibleRows = e.component.getVisibleRows();
    const toIndex = this.items.findIndex(item => item === visibleRows[e.toIndex].data);
    const fromIndex = this.items.findIndex(item => item === e.itemData);

    this.items.splice(fromIndex, 1);
    this.items.splice(toIndex, 0, e.itemData);
    this.recalculateRbr();

    e.component.repaint();
    e.component.refresh();
  };

  isValid(): boolean {
    if (this.inlineEditing && this.datagridExist) {
      this.isValidation = true;
      return this.checkInvalidCells();
    }
    return true;
  }

  checkInvalidCells(): boolean {
    const invalidCells = this.itemsTableValidation.customValidation();
    if (invalidCells?.length) {
      this.highlightInvalidCells(invalidCells);
      return false;
    }
    return true;
  }

  highlightInvalidCells(invalidCells: { rowIndex: number; dataField: string }[]): void {
    invalidCells.forEach(cell => {
      this.addInvalidCellClass(cell);
    });
  }

  addInvalidCellClass(cell: { rowIndex: number; dataField: string }): void {
    const cellElement = this.dataGrid.instance.getCellElement(cell.rowIndex, cell.dataField);
    if (cellElement) {
      if (!cellElement.classList.contains('invalid-cell')) {
        cellElement.classList.add('invalid-cell');
      }
    }
  }

  ngOnDestroy(): void {
    if (!!this.columnHeaders) {
      this.columnHeaders.forEach(header => {
        off(header, 'dxdragstart', this.onColumnDragStart);
        off(header, 'dxdragend', this.onColumnDragEnd);
        header.removeEventListener('click', this.onColumnFixClick);
      });
    }
    this.itemsTableLookupColumnHelper.unsubscribeSearch();
    this.itemsTableHeaderDebugService.close();
  }

  protected readonly BUI_ICON_TYPE_CUSTOM = BUI_ICON_TYPE_CUSTOM;
  protected readonly ModeEnum = ModeEnum;

  clean(): void {
    if (this.items) {
      let i = this.items.length;
      while (i--) {
        if (this.items[i][NEW__]) {
          this.items.splice(i, 1);
        }
      }
    }
  }

  disableRightClick(event: MouseEvent): void {
    event.stopImmediatePropagation();
  }

  screenSizeChanged(screenSizeSmall: boolean): void {
    this.screenSizeSmall = screenSizeSmall;
  }

  fixColumn(column: any, position: string | boolean): void {
    if (position) {
      this.dataGrid.instance.columnOption(column.index, 'fixed', true);
      this.dataGrid.instance.columnOption(column.index, 'fixedPosition', position);
    } else {
      this.dataGrid.instance.columnOption(column.index, 'fixed', false);
    }
    this.itemsTableHeaderPopupService.close();
  }

  getBoundingClientRectTop(): number {
    return this.dataGridWrapperDiv?.nativeElement?.getBoundingClientRect().top || Infinity;
  }

  handleAction(action: ItemActionEnum | string, data: any): void {
    this.tableActions.closeActionsDropdown();
    this.itemActionEvent.emit({action: action, data: data});
  }
}
